import valid from 'card-validator';

/**
 * Bandeiras aceitas pela Cielo, mapeadas do tipo devolvido pela biblioteca
 * `card-validator` (Braintree) para a chave usada no checkout/back-end.
 *
 * Tipos da biblioteca sem equivalente na Cielo (maestro, unionpay, mir)
 * ficam de fora de propósito: o servidor recusa a bandeira com mensagem
 * clara em vez de mandar a cobrança errada ao gateway.
 */
const CIELO_BRANDS = {
    visa: 'visa',
    mastercard: 'mastercard',
    'american-express': 'amex',
    'diners-club': 'diners',
    discover: 'discover',
    jcb: 'jcb',
    elo: 'elo',
    hipercard: 'hipercard',
    hiper: 'hipercard',
};

/**
 * Mantém só os dígitos do número do cartão (remove máscara/espaços).
 *
 * @param {string|number} number número digitado
 * @returns {string}
 */
function onlyDigits(number) {
    return String(number || '').replace(/\D/g, '');
}

/**
 * Detecta a bandeira do cartão pelo número (BIN).
 *
 * Usa `card-validator`, que mantém as faixas atualizadas (Mastercard série
 * 2221–2720, Elo, Hipercard etc. — a regex antiga só aceitava Mastercard
 * 51–55 e devolvia `undefined`, quebrando o checkout: backlog#338).
 * Aura (prefixo 50) não existe na biblioteca e é tratada à parte.
 *
 * @param {string|number} number número do cartão, com ou sem máscara
 * @returns {string|undefined} chave da bandeira ou undefined se desconhecida
 */
export default function validateCreditCard(number) {
    const digits = onlyDigits(number);

    if (digits.length < 4) {
        return undefined;
    }

    const { card } = valid.number(digits);

    if (card && CIELO_BRANDS[card.type]) {
        return CIELO_BRANDS[card.type];
    }

    if (/^50\d{2,}$/.test(digits) && !(card && card.type === 'elo')) {
        return 'aura';
    }

    return undefined;
}

/**
 * Informa se o número completo é válido (tamanho da bandeira + Luhn).
 *
 * @param {string|number} number número do cartão
 * @returns {boolean}
 */
export function isValidCardNumber(number) {
    return valid.number(onlyDigits(number)).isValid;
}
